/**
 * OnboardingOverlay — animated onboarding panel + toggle button.
 *
 * The toggle (QuestionMarkBubble / Close) lives here so that both elements
 * share the same `position:absolute; inset:0` layer and neither affects the
 * flow layout of `.frame`.
 *
 * Animation: panel scales from the top-right corner (where the toggle sits),
 * giving the impression it unfolds out of the icon.
 */

import { useEffect, useRef } from 'react'

import { useOnboarding } from '../../context/OnboardingContext.jsx'

import { Close, QuestionMarkBubble } from '../../../../shared/ui/icons/index.js'

import styles from './OnboardingOverlay.module.scss'

/**
 * @param {{ activeMode: string|null, children: React.ReactNode }} props
 */
export function OnboardingOverlay({ activeMode, children }) {
  const { isOpen, openOnboarding, closeOnboarding } = useOnboarding()
  const backdropRef = useRef(null)

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return
    function onKey(e) {
      if (e.key === 'Escape') closeOnboarding()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, closeOnboarding])

  function handleBackdropClick(e) {
    if (e.target === backdropRef.current) closeOnboarding()
  }

  function handleToggle(e) {
    e.stopPropagation()
    if (isOpen) closeOnboarding()
    else openOnboarding(activeMode)
  }

  const ToggleIcon = isOpen ? Close : QuestionMarkBubble

  return (
    <div
      ref={backdropRef}
      className={`${styles.backdrop} ${isOpen ? styles.backdropVisible : ''}`}
      onClick={handleBackdropClick}
    >
      <div
        className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!isOpen}
      >
        {children}
      </div>
      <button
        type="button"
        className={styles.toggle}
        onClick={handleToggle}
        aria-label={isOpen ? 'Zamknij pomoc' : 'Otwórz pomoc'}
      >
        <ToggleIcon width="1.25em" height="1.25em" />
      </button>
    </div>
  )
}
